import type React from "react";
import {useMemo} from "react";
import {Skeleton, Stack, Typography} from "@mui/material";
import {observer} from "mobx-react-lite";
import {useAgentMetadata} from "~/pages/chat-agent/hooks/useAgentMetadata";
import {AgentLogoIcon} from "./agentLogo";

type Props = {
    /** The agent's `app_name`; falls back to Atlas, then the first agent after `sort_order`. */
    appName?: string;
};

/** The composer's agent: logo and name, no menu (agents are not switched from the composer). */
export const AgentName: React.FC<Props> = observer(({appName}) => {
    const {data: agentsMetadata, isPending, isError} = useAgentMetadata();

    const agent = useMemo(() => {
        if (!agentsMetadata?.length) return null;
        const sorted = [...agentsMetadata].sort((a, b) => a.sort_order - b.sort_order);
        return sorted.find(a => a.app_name === appName)
            ?? sorted.find(a => a.app_name === "atlas")
            ?? sorted[0];
    }, [agentsMetadata, appName]);

    if (isPending) {
        return <Skeleton variant="rounded" width={110} height={40} sx={{borderRadius: 1}}/>;
    }
    if (isError || !agent) {
        return null;
    }
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            aria-label={`Agent: ${agent.display_name}`}
            sx={{
                height: "32px",
                px: "5px",
                flexShrink: 0,
                color: "text.primary",
                "& svg": {display: "block"},
            }}
        >
            <AgentLogoIcon agent={agent}/>
            <Typography component="span" sx={{fontSize: "1rem", fontWeight: 400}}>
                {agent.display_name}
            </Typography>
        </Stack>
    );
});
